// Taşeron portalı — ortak personel çekmecesi (GL5). Paylaşılan `PersonnelSheet` çerçevesinin SP içeriği;
// iki bağlamda AYNI özet, AYNI "Düzenle" ve AYNI belge listesi (`PersonnelDocuments`):
//   - kayıt (Personelim): durum · erişim etiketleri.
//   - izin (`permit` verilir): hazırlık etiketi ÖNCE, sonra durum · erişim, altında "Eksik belgeler"
//     (`VehicleSheet` izin anatomisi, R2). Eksik eğitimler ad ad sayılmaz (mobilde tamamlanır), ama boşluk
//     eğitimdeyse "Eğitim n/m" olgusu görünür — kırmızı etiket nedensiz kalmasın.
// Kişi verisi Personelim listesinden okunur (`supplierContractorKeys.personnel`, sayfayla AYNI önbellek
// girdisi; §13.6 liste satırı `tcKimlikNo` + `isActive` taşır). Tekil `GET` çağrılmaz. Olgular TEK
// ızgaradır (`PersonnelFacts`: Telefon | E-posta / T.C. | tarih), izin hazırlığından sonra, Belgeler'in
// hemen üstünde.
//
// **Düzenleme (#29).** `editor` çekmecenin SAHİBİNDE kurulur (`PersonnelPage` / `PermitPersonnelPanel`:
// `usePersonnelEditor` + `usePersonnelClient`) ve `PersonnelSheet`'e geçer; düzenleme kipinde gövde
// yalnız formdur. Etiket satırındaki "Düzenle" (`PersonnelEditButton`) iki bağlamda da Personelim
// satırından kurulur ve yalnız `toEditTarget` bir hedef verdiğinde (aktive personel, ham `isActive`
// boolean) görünür. Oturumdaki kişinin kendi kaydında Düzenle YOK (sunucu da `409
// PERSONNEL_SELF_TARGET_FORBIDDEN` döner); SEBEBİ iki bağlamda da görünür metindir (R5 — çekmecede
// Tooltip açılamaz). Personelim'de aynı sebeple kendi satırında ⋮ da yoktur.
//
// Çekmecede başka kişi düzeyi eylem YOK (firmadan çıkar / kapsamdan çıkar listenin ⋮'sinde kalır):
// `--z-dropdown` menüsü çekmecenin (`--z-drawer`) perdesinin altında açılırdı.
//
// **Hedef gitti (R3).** Liste geldi ve kişi artık yoksa çekmece boş bir gövde göstermeden kapanır;
// `onTargetGone` önce açıcıyı panelin Ekle/Davet düğmesine çevirir (`useRowRemovalFocus` inişi),
// `#main-content` yalnız `Sheet`'in son çaresidir. Karar yalnız TAZE listeyle verilir: bayat önbellek
// yenilenirken (`isFetching`) eski listede olmayan kişi (ör. izne yeni eklenmiş) yükleniyor gösterir.
// İzin bağlamında kapanma YOK: firmadan çıkarılan kişi #26 korumasına kadar izinde kalır → başlık izin
// satırından, gövde yerinde "bulunamadı" bildirimi (yükleme/belge sorgusu yok).

import {
  Alert,
  type ContractorPersonnelDto,
  type PersonLabelInput,
  PersonnelEditButton,
  type PersonnelEditor,
  PersonnelFacts,
  PersonnelSheet,
  Spinner,
  Tag,
  awaitsActivation,
  currentCulture,
  formatProgress,
  personLabel,
  resolveApiErrorMessage,
  supplierContractorApi,
  toEditTarget,
  useAuthStore,
  useDocumentTypes,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { type RefObject, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../lib/format';
import { supplierContractorKeys } from '../../lib/supplierContractorKeys';
import {
  accessLevelColor,
  personnelStatusColor,
  personnelStatusLabelKey,
  readinessColor,
} from '../status/tone';
import { PersonnelDocuments } from './PersonnelDocuments';

const UNNAMED: PersonLabelInput = {};

export interface PersonnelDrawerProps {
  /** `SupplierUser.Uuid`; `null` → kapalı. */
  userUuid: string | null;
  /** Liste gelene kadar başlık için (izin satırı ad + iletişim taşır). */
  hint?: PersonLabelInput;
  /** Verilirse izin bağlamı — iznin satırındaki hazırlık olguları. */
  permit?: {
    ready: boolean;
    missingDocumentTypes: readonly string[];
    completedTrainingCount: number;
    requiredTrainingCount: number;
    activated?: boolean;
  };
  onOpenChange: (open: boolean) => void;
  /** Kişi listede yok: açıcıyı Ekle/Davet düğmesine çevir (kapanıştan ÖNCE çağrılır). */
  onTargetGone?: () => void;
  /** Açan satırın ad düğmesi — kapanışta odak buraya döner. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /** Düzenleme oturumu (#29) — sahip `usePersonnelEditor` ile kurar. */
  editor: PersonnelEditor;
}

export function PersonnelDrawer({
  userUuid,
  hint,
  permit,
  onOpenChange,
  onTargetGone,
  restoreFocusRef,
  editor,
}: PersonnelDrawerProps) {
  const { t } = useTranslation();
  // Eksik tür etiketi §27 kaydından (kayıtta yoksa ham kod); pasif/bilinmeyen tür karşılanamaz (GL #7).
  const { label: typeLabel, inactiveOrUnknown } = useDocumentTypes();
  const companyId = useAuthStore((s) => s.principal?.companyId ?? null);
  const principalUuid = useAuthStore((s) => s.principal?.uuid ?? null);

  const query = useQuery({
    queryKey: supplierContractorKeys.personnel(companyId),
    queryFn: () => supplierContractorApi.listSupplierContractorPersonnel(),
    enabled: userUuid !== null,
  });
  // Büyük/küçük harf duyarsız (D21): uuid'ler farklı kaynaklardan (izin satırı, gezinme durumu) gelir.
  const row =
    userUuid === null
      ? undefined
      : query.data?.find(
          (candidate) =>
            candidate.kind === 'Personnel' &&
            candidate.uuid.toLowerCase() === userUuid.toLowerCase(),
        );

  useEffect(() => {
    // İzin bağlamında kapanmaz: firmadan çıkarılmış kişi #26'ya kadar izinde kalır → "bulunamadı" bildirimi.
    if (userUuid !== null && query.isSuccess && !query.isFetching && !row && !permit) {
      onTargetGone?.();
      onOpenChange(false);
    }
  }, [userUuid, query.isSuccess, query.isFetching, row, permit, onTargetGone, onOpenChange]);

  // Ad henüz bilinmiyorsa (ipucusuz gezinme açılışı, liste yükleniyor) çerçeve yine gövdeyi çizsin:
  // `PersonnelSheet` gövdeyi yalnız `person` doluyken render eder, boş etiket yükleniyor/hata
  // durumunu görünür kılar (başlık ad gelene kadar boş kalır).
  const person = row ?? hint ?? (userUuid !== null ? UNNAMED : null);
  // İzin bağlamında kişi taze Personelim listesinde yok: başlık izin satırından, gövde bildirim.
  const permitPersonGone =
    permit !== undefined && query.isSuccess && !query.isFetching && userUuid !== null && !row;
  const isSelf =
    row !== undefined &&
    principalUuid !== null &&
    principalUuid.toLowerCase() === row.uuid.toLowerCase();
  // GÜNCEL Personelim satırından; Düzenle `begin`'e onu tıklama anında verir (F2). Kişi listede yoksa
  // (izin bağlamında `permitPersonGone`) ya da kendi kaydıysa hedef yok → Düzenle yok.
  const target = row && !isSelf ? toEditTarget(row) : null;

  return (
    <PersonnelSheet
      open={userUuid !== null}
      onOpenChange={onOpenChange}
      person={person}
      restoreFocusRef={restoreFocusRef}
      editor={editor}
      personUuid={userUuid ?? undefined}
      actions={target ? <PersonnelEditButton editor={editor} target={target} /> : null}
      notice={row ? editor.notice(row.uuid) : null}
      tags={
        row || permitPersonGone ? (
          <>
            {permit ? (
              <Tag color={readinessColor(permit.ready)}>
                {permit.ready ? t('permits.personnel.ready') : t('permits.personnel.notReady')}
              </Tag>
            ) : null}
            {row ? (
              <>
                <Tag color={personnelStatusColor(row.status)}>
                  {t(personnelStatusLabelKey(row))}
                </Tag>
                <Tag color={accessLevelColor(row.accessLevel)}>
                  {t(`personnel.access.${row.accessLevel.toLowerCase()}`)}
                </Tag>
              </>
            ) : null}
          </>
        ) : undefined
      }
      summary={
        row || permitPersonGone ? (
          <>
            {permit && permit.completedTrainingCount < permit.requiredTrainingCount ? (
              <dl>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <dt className="text-xs text-muted">{t('permits.personnel.col.training')}</dt>
                  <dd className="tnum text-sm text-fg">
                    {formatProgress(permit.completedTrainingCount, permit.requiredTrainingCount)}
                  </dd>
                  {awaitsActivation(permit) ? (
                    <dd className="mt-1 flex flex-col gap-1">
                      <Tag color="red">{t('permits.personnel.notActivated')}</Tag>
                      <span className="text-xs text-muted">
                        {t('permits.personnel.notActivated.hint')}
                      </span>
                    </dd>
                  ) : null}
                </div>
              </dl>
            ) : null}
            {permit && permit.missingDocumentTypes.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                <p className="text-xs text-muted">{t('permits.personnel.missingDocuments')}</p>
                <ul className="flex flex-wrap gap-1.5">
                  {permit.missingDocumentTypes.map((type) => (
                    <li
                      key={type}
                      className="flex min-w-0 max-w-full flex-wrap items-baseline gap-x-1.5 gap-y-0.5"
                    >
                      <Tag color="red" wrap>
                        {typeLabel('personnel', type)}
                      </Tag>
                      {inactiveOrUnknown('personnel', type) ? (
                        <span className="shrink-0 text-xs text-muted">
                          {t('documentTypes.inactiveSuffix')}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
                {permit.missingDocumentTypes.some((type) =>
                  inactiveOrUnknown('personnel', type),
                ) ? (
                  <p className="text-xs text-muted">{t('documentTypes.inactiveMissing')}</p>
                ) : null}
              </div>
            ) : null}
            {row ? <PersonnelSummary key={row.uuid} row={row} showSelfNotice={isSelf} /> : null}
          </>
        ) : undefined
      }
      documents={
        row ? (
          <PersonnelDocuments
            key={row.uuid}
            companyId={companyId}
            userUuid={row.uuid}
            personName={personLabel(row)}
          />
        ) : permitPersonGone ? (
          <Alert tone="info">{t('personnel.detail.notFound.body')}</Alert>
        ) : query.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(query.error, t)}</Alert>
        ) : (
          <Spinner label={t('common.loading')} />
        )
      }
    />
  );
}

/**
 * Tek ızgara (`PersonnelFacts`): Telefon | E-posta / T.C. | tarih; tarih hücresi `extra` olarak AYNI
 * ızgaraya girer. T.C. yalnız `TcKimlikValue` ile (maskeli, Göster/Gizle); gösterim durumu çekmece
 * içeriğiyle birlikte söner (kapat-aç ya da kişi değişimi → yine maskeli). Tam değer yalnız açıkken
 * DOM'a girer; `aria-*`/`title`'a ASLA (R10: düğme yalnız alan ETİKETİYLE açıklanır).
 */
function PersonnelSummary({
  row,
  showSelfNotice,
}: {
  row: ContractorPersonnelDto;
  showSelfNotice: boolean;
}) {
  const { t } = useTranslation();
  const culture = currentCulture();
  const tcLabelId = useId();
  return (
    <div className="flex flex-col gap-3">
      <PersonnelFacts
        email={row.email}
        phone={row.phone}
        personalDataMasked={row.personalDataMasked}
        tc={{ value: row.tcKimlikNo, labelId: tcLabelId }}
        extra={
          <div className="min-w-0">
            <dt className="text-xs font-medium text-muted">
              {row.status === 'NoLogin'
                ? t('personnel.detail.field.createdAt')
                : t('personnel.detail.field.activatedAt')}
            </dt>
            <dd className="tnum mt-0.5 text-sm text-fg">{formatDate(row.activatedAt, culture)}</dd>
          </div>
        }
      />
      {row.status === 'NoLogin' ? (
        <p className="text-xs text-muted">{t('personnel.detail.noLogin.body')}</p>
      ) : null}
      {showSelfNotice ? (
        <p className="text-xs text-muted">{t('personnel.detail.selfNoticeBody')}</p>
      ) : null}
    </div>
  );
}
